/**
 * Service layer — the Pulse workspace's firm-level reads (Performance / Efficiency / Anatomy).
 *
 * Assignments are NOT here. RASCI was retired 2026-09-02 (docs/FLOW_RECONCILIATION_PLAN.md §0):
 * crews are the only assignment mechanism, they live in Supabase (`flights` / `flight_crew`), and
 * every surface reads them through outline/crew.js. What remains here is firm-shape data — team
 * capacity, practice roles, phase efficiency, aging/posted milestones, snapshots, and the skill
 * layers behind the Anatomy tab.
 *
 * No fields[] — Contacts / Phase / Deliverable rows carry lookups (project, member id, capacity,
 * dates) that return empty when restricted. Each set is low-hundreds of rows; apiGetAll paginates
 * via the queue.
 */

import { apiGet, apiGetAll, apiGetOne, apiPatch } from './_api.js'
import { enrichPhaseRecords } from './phaseFinancials.js'

const TBL_CONTACTS = 'tblEcywoAVDwkOLiK'
const TBL_ROLES    = 'tblq1neInwg9utMpU'   // Practice Roles (the firm's real role structure)
const TBL_PHASES   = 'tblNl3JpfIEWk9tPX'   // Phases
const TBL_DEL      = 'tblFuiDpahqCL4eIK'   // Project Deliverables (milestones)

/**
 * The firm's real Practice Roles (Director, Studio Director, Designer, BIM Technician, …) with their
 * hierarchy + functional lane. Returns { roles: [{id,name,hierarchy,lane}], byId: {id:name} } so the
 * team model can resolve each Contact's Practice Roles link (which returns record IDs) to names, and
 * the Role-skills panel can list the actual roles (even unstaffed ones).
 */
export async function getPracticeRoles() {
  const recs = await apiGet(TBL_ROLES, 'maxRecords=200')
  const roles = (recs || []).map(r => {
    const f = r.fields || {}
    return { id: r.id, name: f['Name'] || '', hierarchy: Number(f['Heirarchy']) || 999, lane: f['Functional Lane'] || '' }
  }).filter(r => r.name).sort((a, b) => a.hierarchy - b.hierarchy)
  const byId = {}
  roles.forEach(r => { byId[r.id] = r.name })
  return { roles, byId }
}

/** Team contacts with capacity fields (Max Concurrent / Active Milestone Count / Days Budgeted Impossible). */
export function getTeamCapacity() {
  return apiGet(TBL_CONTACTS, 'filterByFormula=' + encodeURIComponent("{Contact Type}='Team'") + '&maxRecords=100')
}

/**
 * Active phases with their budget-vs-actual + activity fields, for the efficiency / constraint finder
 * (CT-025 Build 2). Hours Budget / Percent Complete / Open Milestones are read straight off Airtable
 * (unaffected by the time-entries cutover); Hours Expended / Fee Performance / Most Recent Time Entry
 * are corrected against live Supabase time entries before returning — the Airtable rollups they used
 * to read are frozen since the cutover (services/phaseFinancials.js, docs/MIGRATION_DERIVATIONS.md).
 * Executed projects only, not-complete phases with a real hours budget. No fields[] (lookups like
 * Project Name / Contract Stage null out under it).
 */
export async function getPhaseEfficiency(onPage) {
  const formula = "AND({Contract Stage (from Project)}='Executed',{Phase Complete}!=1,{Hours Budget}>0)"
  // Wraps the progressive callback so each page the caller sees is already corrected — apiGetAll
  // fires onPage with the full accumulated slice so far, not just the new page, and doesn't await it
  // (best-effort progressive render; the final return below is always the authoritative pass).
  const wrappedOnPage = onPage
    ? (records, isLast) => {
        enrichPhaseRecords(records)
          .then(enriched => onPage(enriched, isLast))
          .catch(e => { console.error('[teamOverview] phase enrichment failed', e && e.message); onPage(records, isLast) })
      }
    : null
  const records = await apiGetAll(TBL_PHASES, 'filterByFormula=' + encodeURIComponent(formula), wrappedOnPage)
  try { return await enrichPhaseRecords(records) }
  catch (e) { console.error('[teamOverview] phase enrichment failed', e && e.message); return records }
}

/**
 * Aging WIP — open milestones that have already started (Earliest Start in the past) but aren't posted,
 * for the work-item-age view (Lean/Kanban flow). No fields[] (Project Name / dates are lookups).
 */
export function getAgingMilestones(onPage) {
  const formula = "AND({Posted Date}='',{Contract Stage (from Project)}='Executed',{Earliest Start (EST)}!='',IS_BEFORE({Earliest Start (EST)},TODAY()))"
  return apiGetAll(TBL_DEL, 'filterByFormula=' + encodeURIComponent(formula), onPage)
}

/**
 * Recently posted milestones (last ~120 days) with a start date, for cycle-time (start → posted).
 * Backfillable — both dates already exist, no new capture needed. No fields[] (date/name lookups).
 */
export function getRecentlyPosted(onPage) {
  const since = new Date(Date.now() - 120 * 86400000).toISOString().slice(0, 10)
  const formula = `AND({Posted Date}!='',IS_AFTER({Posted Date},'${since}'),{Earliest Start (EST)}!='')`
  return apiGetAll(TBL_DEL, 'filterByFormula=' + encodeURIComponent(formula), onPage)
}

/**
 * Latest Practice Performance snapshot (measured Load / momentum) from Supabase, via the
 * manager-gated API route. Returns { snapshotDate, members:[...], projects:[...] }; members keyed by
 * Member record id (member_id), the same id the crew index and the skill layers use.
 */
export async function getLatestSnapshots() {
  return practiceSnapshot({ action: 'latest' })
}

/**
 * Snapshot history (last `days`) for the firm-level trend charts: { members, projects, runs }.
 * Each array is ordered oldest→newest. Manager-gated (same route).
 */
export async function getSnapshotHistory(days = 90) {
  return practiceSnapshot({ action: 'history', days })
}

/**
 * Milestone detail for the guided-resolution classifier (CT-025): the milestone record plus its
 * predecessor's posted state (to detect "blocked upstream"). One record GET + one for the predecessor.
 * No fields[] — the milestone carries lookups (agency status, checklist %, project) that null under it.
 */
export async function getMilestoneResolution(msId) {
  const ms = await apiGetOne(TBL_DEL, msId)
  if (!ms) throw new Error('Milestone not found')
  const f = ms.fields || {}
  const predId = Array.isArray(f['Predecessor']) ? f['Predecessor'][0] : null
  let predecessor = null
  if (predId) {
    try {
      const p = await apiGetOne(TBL_DEL, predId)
      const pf = (p && p.fields) || {}
      const postedVal = Array.isArray(pf['Posted']) ? pf['Posted'][0] : pf['Posted']
      const name = String((Array.isArray(pf['Full Deliverable Name']) ? pf['Full Deliverable Name'][0] : pf['Full Deliverable Name']) || (Array.isArray(pf['Name']) ? pf['Name'][0] : pf['Name']) || 'the upstream item')
      predecessor = { id: predId, name, posted: !!postedVal || !!pf['Posted Date'] }
    } catch (e) { /* predecessor fetch is best-effort */ }
  }
  return { id: msId, fields: f, predecessor }
}

/**
 * Mark a milestone reactive (waiting on an outside party) by zeroing Days Budgeted — the scheduler's
 * own definition: it then drops out of active scheduling + the team's capacity and is tracked as
 * "waiting on outside parties". Reversible (set a budget again to reactivate).
 */
export function markMilestoneReactive(msId) {
  return apiPatch(TBL_DEL, msId, { 'Days Budgeted': 0 })
}

/**
 * Push a milestone's deadline out by `days` from today — the easy "give the outside party N days to
 * finish" move on a reactive item. Sets a committed (pinned) Latest Finish so the autoscheduler holds
 * it; items that depend on it will re-derive from the new date. Appends a timestamped audit line to
 * the milestone's Notes so the change is logged in our current data structure. Returns the new date.
 * @param {object} opts { by, prevLft, prevNotes }
 */
export function pushMilestoneDeadline(msId, days, opts = {}) {
  const d = new Date(); d.setDate(d.getDate() + Number(days))
  const ymd = d.toISOString().slice(0, 10)
  const today = new Date().toISOString().slice(0, 10)
  const from = opts.prevLft ? String(opts.prevLft).slice(0, 10) : '—'
  const stamp = `[${today}] Deadline pushed +${days}d (${from} → ${ymd}), pinned — waiting on outside party · ${opts.by || 'a manager'}`
  const prev = (opts.prevNotes || '').trim()
  const Notes = prev ? prev + '\n' + stamp : stamp
  return apiPatch(TBL_DEL, msId, { 'Latest Finish (LFT)': ymd, 'Date Pinned': true, Notes }).then(() => ymd)
}

/**
 * Send a friendly "we're waiting on you" heads-up to an outside party via the manager-gated
 * /api/milestone-nudge route (Resend). The user supplies + confirms the recipient and message; the
 * server adds the milestone/project subject. Never auto-sent.
 */
export async function sendMilestoneNudge(msId, to, message) {
  const token = (typeof window !== 'undefined' && window.__clerkGetToken) ? await window.__clerkGetToken() : null
  const res = await fetch('/api/milestone-nudge', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify({ msId, to, message }),
  })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`)
  return data
}

/**
 * Pull a milestone's deadline IN by `days` — the inverse of pushMilestoneDeadline. Moves the
 * committed finish earlier (computed from the current LFT, opts.prevLft — NOT today) and pins it so
 * the scheduler holds it; the scheduler reconciles dependents around the pin. Logs a timestamped
 * audit line to Notes. Returns the new date.
 */
export function pullMilestoneDeadline(msId, days, opts = {}) {
  if (!opts.prevLft) throw new Error('pullMilestoneDeadline needs the current Latest Finish (prevLft)')
  const d = new Date(String(opts.prevLft).slice(0, 10) + 'T00:00:00Z')
  d.setUTCDate(d.getUTCDate() - Number(days))
  const ymd = d.toISOString().slice(0, 10)
  const today = new Date().toISOString().slice(0, 10)
  const from = String(opts.prevLft).slice(0, 10)
  const stamp = `[${today}] Deadline pulled in ${days}d (${from} → ${ymd}), pinned · ${opts.by || 'a manager'}`
  const prev = (opts.prevNotes || '').trim()
  const Notes = prev ? prev + '\n' + stamp : stamp
  return apiPatch(TBL_DEL, msId, { 'Latest Finish (LFT)': ymd, 'Date Pinned': true, Notes }).then(() => ymd)
}

/**
 * Defer a milestone — de-prioritize it by pushing its committed finish out by `days` (default 30)
 * from today and pinning, so the scheduler sequences deadline-critical work ahead of it. Keeps Days
 * Budgeted intact (it is still our work, just later) — distinct from markMilestoneReactive (which
 * zeroes the budget for work waiting on an outside party). Logs an audit line. Returns the new date.
 */
export function deferMilestone(msId, days = 30, opts = {}) {
  const d = new Date(); d.setDate(d.getDate() + Number(days))
  const ymd = d.toISOString().slice(0, 10)
  const today = new Date().toISOString().slice(0, 10)
  const from = opts.prevLft ? String(opts.prevLft).slice(0, 10) : '—'
  const stamp = `[${today}] Deferred +${days}d (${from} → ${ymd}), pinned — de-prioritized so deadline-critical work fits · ${opts.by || 'a manager'}`
  const prev = (opts.prevNotes || '').trim()
  const Notes = prev ? prev + '\n' + stamp : stamp
  return apiPatch(TBL_DEL, msId, { 'Latest Finish (LFT)': ymd, 'Date Pinned': true, Notes }).then(() => ymd)
}

/**
 * Member skills (the editable eligibility layer). Returns an object keyed by Airtable Member id →
 * [{ id, skill, proficiency }] so the eligibility matcher (outline/eligibility.js) can look a person
 * up by the same id the team model uses. Best-effort: an empty result just means eligibility falls
 * back to Practice Roles + availability.
 */
export async function getMemberSkills() {
  const { skills } = await memberSkills({ action: 'list' })
  const by = {}
  ;(skills || []).forEach(s => {
    (by[s.member_airtable_id] = by[s.member_airtable_id] || []).push({ id: s.id, skill: s.skill, proficiency: s.proficiency })
  })
  return by
}

export function saveMemberSkill(memberAirtableId, memberName, skill, proficiency) {
  return memberSkills({ action: 'upsert', memberAirtableId, memberName, skill, proficiency })
}

export function deleteMemberSkill(id) { return memberSkills({ action: 'delete', id }) }

/**
 * Role → expected skills (the heavy eligibility signal). Returns an object keyed by role name →
 * [{ id, skill, proficiency }] so the matcher can look up a person's role(s). Low upkeep: defined
 * once per role, inherited by everyone in it.
 */
export async function getRoleSkills() {
  const { roleSkills } = await memberSkills({ action: 'roleList' })
  const by = {}
  ;(roleSkills || []).forEach(s => {
    (by[s.role] = by[s.role] || []).push({ id: s.id, skill: s.skill, proficiency: s.proficiency })
  })
  return by
}

export function saveRoleSkill(role, skill, proficiency) {
  return memberSkills({ action: 'roleUpsert', role, skill, proficiency })
}

export function deleteRoleSkill(id) { return memberSkills({ action: 'roleDelete', id }) }

/**
 * Demonstrated skills (learned from real work — table 073). Returns an object keyed by Member id →
 * [{ skill, level, confidence }] for the matcher's demonstrated layer + the RPG skill sheet.
 */
export async function getDemonstratedSkills() {
  const { demonstrated } = await memberSkills({ action: 'demonstratedList' })
  const by = {}
  ;(demonstrated || []).forEach(s => {
    (by[s.member_airtable_id] = by[s.member_airtable_id] || []).push({ skill: s.skill, level: s.level, confidence: s.confidence })
  })
  return by
}

async function memberSkills(body) {
  const token = (typeof window !== 'undefined' && window.__clerkGetToken) ? await window.__clerkGetToken() : null
  const res = await fetch('/api/member-skills', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(body),
  })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`)
  return data
}

async function practiceSnapshot(body) {
  const token = (typeof window !== 'undefined' && window.__clerkGetToken) ? await window.__clerkGetToken() : null
  const res = await fetch('/api/practice-snapshot', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(body),
  })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`)
  return data
}
